import React, {
  useState,
  useEffect,
  useRef,
  useCallback,
} from "react";
import { Controller } from "@hotwired/stimulus";
import { createRoot } from "react-dom/client";
import { createConsumer } from "@rails/actioncable";
import type { Subscription, Consumer } from "@rails/actioncable";
import axios from "axios";
import { Spinner } from "@ui";
import { RefreshCw, Code, X, AlertTriangle, Sparkles } from "lucide-react";
import { Streamdown } from "streamdown";
import { markdownComponents } from "@agentic_conversation/pages/chat/components/markdown-components";
import { StreamingErrorBoundary } from "@agentic_conversation/pages/chat/components/error-boundary";
import { ChatProvider } from "@agentic_conversation/pages/chat/context";

// ---------------------------------------------------------------------------
// React component — owns ActionCable, typewriter, and Streamdown rendering
// ---------------------------------------------------------------------------

type Phase = "loading" | "streaming" | "done" | "error";

interface Props {
  agentId: number;
  profileId: number;
  profileType: string;
  initialContent: string | null;
  createUrl: string;
  drawerUrl: string | null;
  initialDebugHtml: string | null;
  signupUrl: string | null;
}

function ProfileEntrypointChat({
  agentId,
  profileId,
  profileType,
  initialContent,
  createUrl,
  drawerUrl,
  initialDebugHtml,
  signupUrl,
}: Props) {
  const [phase, setPhase] = useState<Phase>(
    initialContent ? "streaming" : "loading"
  );
  const [displayedContent, setDisplayedContent] = useState("");
  const [errorMessage, setErrorMessage] = useState("");
  const [debugHtml, setDebugHtml] = useState(initialDebugHtml || "");
  const [debugOpen, setDebugOpen] = useState(false);

  const fullContentRef = useRef(initialContent || "");
  const typewriterRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const subscriptionRef = useRef<Subscription | null>(null);
  const consumerRef = useRef<Consumer | null>(null);

  // -- Cleanup cable --
  const cleanupCable = useCallback(() => {
    subscriptionRef.current?.unsubscribe();
    consumerRef.current?.disconnect();
    subscriptionRef.current = null;
    consumerRef.current = null;
  }, []);

  // -- Typewriter --
  const CHUNK = 12;
  const DELAY = 8;

  const startTypewriter = useCallback(
    (text: string, onComplete?: () => void) => {
      let offset = 0;
      const tick = () => {
        offset = Math.min(offset + CHUNK, text.length);
        setDisplayedContent(text.slice(0, offset));
        if (offset < text.length) {
          typewriterRef.current = setTimeout(tick, DELAY);
        } else {
          typewriterRef.current = null;
          onComplete?.();
        }
      };
      tick();
    },
    []
  );

  const cancelTypewriter = useCallback(() => {
    if (typewriterRef.current !== null) {
      clearTimeout(typewriterRef.current);
      typewriterRef.current = null;
    }
  }, []);

  // -- Subscribe to ActionCable --
  const subscribeToCable = useCallback(
    (chatId: number, token: string) => {
      cleanupCable();
      const consumer = createConsumer();
      consumerRef.current = consumer;

      subscriptionRef.current = consumer.subscriptions.create(
        { channel: "ProfileEntrypoint::ChatChannel", chat_id: chatId, token },
        {
          received(data: {
            type: string;
            content?: string;
            error?: string;
          }) {
            if (data.type === "message_completed") {
              const content = data.content || "";
              fullContentRef.current = content;
              setPhase("streaming");
              startTypewriter(content, () => {
                setPhase("done");
                refreshDebugPanel();
              });
              cleanupCable();
            }

            if (data.type === "message_error") {
              setErrorMessage(data.error || "La génération a échoué.");
              setPhase("error");
              cleanupCable();
            }
          },
        }
      );
    },
    [cleanupCable, startTypewriter]
  );

  // -- Debug panel refresh (system only) --
  const refreshDebugPanel = useCallback(async () => {
    if (!drawerUrl) return;
    try {
      const url = `${drawerUrl}?agent_id=${agentId}&profile_type=${encodeURIComponent(profileType)}&profile_id=${profileId}`;
      const resp = await fetch(url, { headers: { Accept: "text/html" } });
      const html = await resp.text();
      const doc = new DOMParser().parseFromString(html, "text/html");
      const newDebug = doc.querySelector(
        "[data-profile-entrypoint-chat-target='debugTemplate']"
      );
      if (newDebug) {
        setDebugHtml(newDebug.innerHTML);
      }
    } catch {
      // best-effort
    }
  }, [drawerUrl, agentId, profileType, profileId]);

  // -- Create chat and start listening --
  const createAndStream = useCallback(async () => {
    setPhase("loading");
    setDisplayedContent("");
    setErrorMessage("");
    setDebugOpen(false);
    fullContentRef.current = "";

    try {
      const { data } = await axios.post(createUrl, {
        agent_id: agentId,
        profile_type: profileType,
        profile_id: profileId,
      });
      setPhase("streaming");
      subscribeToCable(data.chat_id, data.chat_token);
    } catch {
      setErrorMessage("Impossible de lancer la génération.");
      setPhase("error");
    }
  }, [
    createUrl,
    agentId,
    profileType,
    profileId,
    subscribeToCable,
  ]);

  // -- Initial mount --
  useEffect(() => {
    if (initialContent) {
      startTypewriter(initialContent, () => setPhase("done"));
    } else {
      createAndStream();
    }
    return () => {
      cancelTypewriter();
      cleanupCable();
    };
    // Run only on mount
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // -- Regenerate --
  const handleRegenerate = useCallback(() => {
    cancelTypewriter();
    cleanupCable();
    createAndStream();
  }, [cancelTypewriter, cleanupCable, createAndStream]);

  const isAnimating = phase === "streaming" && displayedContent.length > 0;
  const noop = useCallback(() => {}, []);

  return (
    <ChatProvider sendUserMessageFromSuggestion={noop}>
      <div className="flex flex-col flex-1 h-full">
        {/* Loading / waiting for generation */}
        {(phase === "loading" || (phase === "streaming" && displayedContent.length === 0)) && (
          <div className="flex-1 flex flex-col items-center justify-center gap-3 p-24">
            <Sparkles className="w-12 h-12 text-default-400 animate-pulse" />
            <span className="text-lg font-medium text-default-400">Analyse en cours…</span>
          </div>
        )}

        {/* Content */}
        {displayedContent && (
          <div className="p-6 text-slate-600 max-w-none">
            <StreamingErrorBoundary>
              <Streamdown
                components={markdownComponents}
                isAnimating={isAnimating}
                controls={{ code: true, table: false }}
              >
                {displayedContent}
              </Streamdown>
            </StreamingErrorBoundary>
          </div>
        )}

        {/* Error */}
        {phase === "error" && (
          <div className="p-6">
            <div className="bg-red-50 text-red-700 p-4 rounded-lg flex items-start gap-2">
              <AlertTriangle className="w-4 h-4 mt-0.5 flex-shrink-0" />
              <p>{errorMessage}</p>
            </div>
          </div>
        )}

        {/* Signup CTA (anonymous visitors only, after content arrives) */}
        {signupUrl && phase === "done" && (
          <div className="-mt-[200px] relative z-10 opacity-0 animate-[slideUpFadeIn_0.5s_ease-out_1.5s_forwards]">
            <div className="h-[200px] bg-gradient-to-t from-white from-40% to-transparent pointer-events-none" />
            <div className="bg-white p-6">
              <a
                href={signupUrl}
                data-turbo-frame="_top"
                className="flex items-center justify-center gap-2 w-full px-6 py-4 text-lg font-semibold text-white bg-default-500 rounded-xl hover:bg-default-600 transition-colors"
              >
                <span>→</span>
                S'inscrire pour continuer la conversation
              </a>
            </div>
          </div>
        )}

        {/* Regenerate + Debug buttons (system only) */}
        {drawerUrl && (phase === "done" || phase === "error") && (
          <div className="px-6 pb-4">
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={handleRegenerate}
                className="inline-flex items-center gap-2 px-3 py-1.5 text-sm font-medium text-gray-600 bg-gray-50 border border-gray-200 rounded-lg hover:bg-gray-100 transition-colors"
              >
                <RefreshCw className="w-4 h-4" />
                Régénérer
              </button>
              {debugHtml && (
                <button
                  type="button"
                  onClick={() => setDebugOpen((v) => !v)}
                  className={`inline-flex items-center gap-2 px-3 py-1.5 text-sm font-medium border rounded-lg transition-colors ${
                    debugOpen
                      ? "text-default-600 bg-default-50 border-default-200"
                      : "text-gray-400 bg-white border-gray-200 hover:bg-gray-50"
                  }`}
                >
                  {debugOpen ? <X className="w-4 h-4" /> : <Code className="w-4 h-4" />}
                  Debug
                </button>
              )}
            </div>
          </div>
        )}

        {/* Debug panel (system only) — rendered inline below buttons */}
        {debugOpen && debugHtml && (
          <div className="px-6 pb-6">
            <div dangerouslySetInnerHTML={{ __html: debugHtml }} />
          </div>
        )}
      </div>
    </ChatProvider>
  );
}

// ---------------------------------------------------------------------------
// Stimulus controller — thin mount/unmount bridge
// ---------------------------------------------------------------------------

export default class extends Controller {
  static values = {
    chatId: Number,
    agentId: Number,
    profileId: Number,
    profileType: String,
    hasContent: Boolean,
    initialContent: String,
    createUrl: String,
    drawerUrl: String,
    signupUrl: String,
  };

  declare agentIdValue: number;
  declare profileIdValue: number;
  declare profileTypeValue: string;
  declare hasContentValue: boolean;
  declare initialContentValue: string;
  declare createUrlValue: string;
  declare drawerUrlValue: string;
  declare signupUrlValue: string;

  static targets = ["debugTemplate", "reactRoot"];
  declare reactRootTarget: HTMLElement;
  declare debugTemplateTarget: HTMLElement;
  declare hasDebugTemplateTarget: boolean;

  private root: ReturnType<typeof createRoot> | null = null;

  connect() {
    this.root = createRoot(this.reactRootTarget);
    this.root.render(
      <ProfileEntrypointChat
        agentId={this.agentIdValue}
        profileId={this.profileIdValue}
        profileType={this.profileTypeValue}
        initialContent={this.hasContentValue ? this.initialContentValue : null}
        createUrl={this.createUrlValue}
        drawerUrl={this.drawerUrlValue || null}
        initialDebugHtml={this.hasDebugTemplateTarget ? this.debugTemplateTarget.innerHTML : null}
        signupUrl={this.signupUrlValue || null}
      />
    );
  }

  disconnect() {
    if (this.root) {
      this.root.unmount();
      this.root = null;
    }
  }
}
